import { CloseIcon, RefreshIcon, SearchIcon } from "@/components/icons";
import type { LogLevelFilter, LogLevelCounts } from "../model/task-log-view";
import styles from "./TaskLogViewer.module.css";

type TaskLogToolbarProps = {
  logCount: number;
  isFetching: boolean;
  isLive: boolean;
  isTaskActive: boolean;
  level: LogLevelFilter;
  levelCounts: LogLevelCounts;
  onLevelChange: (level: LogLevelFilter) => void;
  onLiveToggle: () => void;
  onQueryChange: (query: string) => void;
  onRefresh: () => void;
  query: string;
};

export function TaskLogToolbar({
  logCount,
  isFetching,
  isLive,
  isTaskActive,
  level,
  levelCounts,
  onLevelChange,
  onLiveToggle,
  onQueryChange,
  onRefresh,
  query,
}: TaskLogToolbarProps) {
  const filters = [
    ["all", "All", logCount],
    ["Error", "Errors", levelCounts.Error],
    ["Warning", "Warnings", levelCounts.Warning],
    ["Information", "Info", levelCounts.Information],
  ] as const;

  return (
    <div className={styles.toolbar}>
      <label className={styles.searchField}>
        <SearchIcon />
        <input
          type="search"
          aria-label="Search task logs"
          placeholder="Search messages or timestamps"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
        />
        {query ? (
          <button type="button" onClick={() => onQueryChange("")} aria-label="Clear search">
            <CloseIcon />
          </button>
        ) : null}
      </label>
      <div className={styles.filterBar} role="group" aria-label="Filter logs by level">
        {filters.map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            className={styles.filterButton}
            aria-pressed={level === value}
            onClick={() => onLevelChange(value)}
          >
            {label}
            <span>{count.toLocaleString()}</span>
          </button>
        ))}
      </div>
      <div className={styles.liveControls}>
        <button
          type="button"
          className={isLive && isTaskActive ? styles.liveButton : undefined}
          onClick={onLiveToggle}
          disabled={!isTaskActive}
          title={
            !isTaskActive
              ? "Task is not currently running"
              : isLive
                ? "Pause live updates"
                : "Resume live updates"
          }
        >
          <span className={styles.liveDot} aria-hidden="true" />
          {isTaskActive ? (isLive ? "Live" : "Paused") : "Settled"}
        </button>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          title="Refresh now"
          aria-label="Refresh logs"
        >
          <span className={isFetching ? styles.spinning : undefined}>
            <RefreshIcon />
          </span>
        </button>
      </div>
    </div>
  );
}
